import React from 'react';
import { Box } from '@mui/material';
import GapsRisksPanel, { GAPS_PANEL_WIDTH, TeamsSelect, type GapsRisksPanelProps } from './GapsRisksPanel';
import DevelopmentTierPanel from '../../../../../../components/gamification/DevelopmentTierPanel';
import { type MedalsData } from '../../../../../../components/gamification/MedalScore';
import TeamActivitySections from './TeamActivitySections';
import { teamActivity } from './teamActivity';
import { activitySections } from './activityMeasures';
import { useCapabilityActivity, useCapabilityActivityHistory } from '../../../../../../queries/useCapability';
import { parseTierHistory } from '../../../../../../components/gamification/scorecardData';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

export type InsightsView = 'gaps' | 'tier';

const BRANDY_FRAME = 'rgba(247, 148, 32, 0.45)';
const SURFACE = '#1C1C1C';

type Props = {
  view: InsightsView;
  onViewChange: (view: InsightsView) => void;
  /** The org's tier for the selected teams. */
  tierMedals: MedalsData | null;
  tierLoading: boolean;
  audience: 'orgAdmin' | 'groupAdmin';
} & Omit<GapsRisksPanelProps, 'embedded' | 'headerLeft'>;

/**
 * Capability / Activity — two section titles in the same type as
 * LEARNING FRAMEWORK (all three are equal sections). The active one is white,
 * the other dimmed; the tier's explainer sits behind the (i) beside its title.
 */
export const ViewTabs: React.FC<{
  view: InsightsView;
  onChange: (view: InsightsView) => void;
  /** A learner's own panel: plain "Capability" and "Activity" (naming rule 9's exception). */
  learner?: boolean;
}> = ({ view, onChange, learner = false }) => {
  const tab = (key: InsightsView, label: string) => {
    const on = view === key;
    return (
      <Box
        component="button"
        type="button"
        role="tab"
        aria-selected={on}
        onClick={() => onChange(key)}
        sx={{
          p: 0,
          pb: '3px',
          border: 'none',
          // Brandy underline marks the active view.
          borderBottom: `2px solid ${on ? '#F79420' : 'transparent'}`,
          bgcolor: 'transparent',
          cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
          fontSize: 17,
          fontWeight: 700,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          color: on ? '#FDFDFD' : '#868686',
          transition: 'color 0.15s',
          '&:hover': { color: on ? '#FDFDFD' : '#B6B6B6' },
          // Phone: a size that leaves the header's icons their room, and a finger-sized
          // target around the title without moving the underline.
          [MOBILE_SX]: {
            fontSize: 15,
            position: 'relative',
            '&::after': { content: '""', position: 'absolute', inset: '-10px -5px' }
          }
        }}
      >
        {label}
      </Box>
    );
  };
  return (
    <Box
      role="tablist"
      sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0, [MOBILE_SX]: { gap: '10px' } }}
    >
      {tab('gaps', learner ? 'Capability' : 'Team Capability')}
      {/* Subtle brandy divide between the two sections. */}
      <Box sx={{ width: '1px', height: 18, flexShrink: 0, bgcolor: 'rgba(247, 148, 32, 0.4)' }} />
      {tab('tier', learner ? 'Activity' : 'Team Activity')}
    </Box>
  );
};

/**
 * Admin dashboard left panel — Learning Gaps or the org's Learning Tier, in the
 * same brandy frame as the learner's tier panel and split off from the Learning
 * Framework. One Teams filter drives both views, so switching keeps the selection.
 */
const AdminInsightsPanel: React.FC<Props> = ({
  view,
  onViewChange,
  tierMedals,
  tierLoading,
  audience,
  ...gapsProps
}) => {
  const tabs = <ViewTabs view={view} onChange={onViewChange} />;
  // Every team selected is the admin's whole remit: no filter.
  const { selectedTeamIds, teamOptions } = gapsProps;
  const teamIds = selectedTeamIds.length > 0 && selectedTeamIds.length < teamOptions.length ? selectedTeamIds : null;
  // Roleplays and Practice Rounds, and every measure's leaders, from the Capability backend.
  const { data: capabilityActivity, isError: activityFailed } = useCapabilityActivity(teamIds, view === 'tier');
  // Last 6 Months: the teams' Activity % at the end of each week (the backend rebuilds it).
  const { data: activityHistory } = useCapabilityActivityHistory(teamIds, view === 'tier');
  // The Activity tab's five measures, in order (see activityMeasures.ts), and the tier they average to.
  const activityMedals = tierMedals
    ? { ...tierMedals, sections: activitySections(tierMedals.sections, capabilityActivity?.sections) }
    : tierMedals;
  const activity = teamActivity(activityMedals?.sections ?? []);
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: SURFACE,
        border: `1px solid ${BRANDY_FRAME}`,
        borderRadius: '8px',
        boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)'
      }}
    >
      {view === 'gaps' ? (
        <GapsRisksPanel {...gapsProps} embedded headerLeft={tabs} />
      ) : (
        <>
          {/* Same header row as the Gaps view, so the titles and filter don't move when switching. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              px: '14px',
              py: '10px',
              minHeight: 56,
              borderBottom: '1px solid #2A2A2A',
              flexShrink: 0,
              [MOBILE_SX]: { px: '12px', gap: '4px' }
            }}
          >
            {tabs}
            <Box sx={{ flex: 1 }} />
            {gapsProps.teamOptions.length > 0 && (
              <TeamsSelect
                teamOptions={gapsProps.teamOptions}
                selectedTeamIds={gapsProps.selectedTeamIds}
                onTeamsChange={gapsProps.onTeamsChange}
              />
            )}
          </Box>
          <DevelopmentTierPanel
            embedded
            audience={audience}
            medals={activityMedals}
            loading={tierLoading}
            actionsByMeasure={{}}
            actionsLoading={false}
            intro={
              <TeamActivitySections
                activity={activity}
                history={activityHistory ? parseTierHistory(activityHistory.history) : undefined}
              />
            }
            leaderboardScope={gapsProps.selectedTeamIds.join('|')}
            // Undefined until it arrives (Leaders says so); null once it could not be read.
            leaders={capabilityActivity?.leaders ?? (activityFailed ? null : undefined)}
          />
        </>
      )}
    </Box>
  );
};

export { GAPS_PANEL_WIDTH };
export default AdminInsightsPanel;
